import { useCallback, useEffect, useState } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'

import axios from 'axios'

import { DEFAULT_IMAGE_GALLERY_FILTERS } from 'Pods/LazyLoaded/ImageGalleryPanel/constants'

export const PER_PAGE = 30
export const SEARCH_DEBOUNCE_MS = 500

const COMMUNITY_GALLERY_ENDPOINT = '/api/internal/generator/community-gallery'
const RUN_TYPE = 'image_generation'

// Runs whose output blob has not been analyzed yet carry no aspect ratio; those cards
// render square (the card crops with `object-fit: cover`).
const DEFAULT_ASPECT_RATIO = 1

// The backend sends the ratio as "width:height" (e.g. "3:2").
const toAspectRatio = (aspectRatio) => {
  const [width, height] = String(aspectRatio ?? '').split(':').map(Number)
  return width > 0 && height > 0 ? width / height : DEFAULT_ASPECT_RATIO
}

const toImage = ({ image, aspectRatio, ...rest }) => ({
  ...rest,
  src: image,
  aspectRatio: toAspectRatio(aspectRatio),
})

// Runs without a signed image URL have nothing to render, so they are dropped.
const toPage = response => ({
  items: (response?.data ?? []).filter(item => item.image).map(toImage),
  meta: response?.meta ?? {},
})

const fetchCommunityImages = async ({ page, searchQuery, category }) => {
  const { data } = await axios.get(COMMUNITY_GALLERY_ENDPOINT, {
    params: {
      page,
      per_page: PER_PAGE,
      run_type: RUN_TYPE,
      search_query: searchQuery || undefined,
      category: category === DEFAULT_IMAGE_GALLERY_FILTERS.category ? undefined : category,
    },
  })
  return toPage(data)
}

const getNextPageParam = (lastPage, allPages) => {
  const { currentPage, totalPages } = lastPage.meta
  return currentPage < totalPages ? allPages.length + 1 : undefined
}

// Typing should not fire a request per keystroke; clearing applies at once so
// "Clear Filters" snaps back to the default set instead of waiting out the delay.
function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    if (!value) {
      setDebounced(value)
      return undefined
    }
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])

  return debounced
}

// Paged community images for the given filters.
function useGalleryImages({
  searchQuery = DEFAULT_IMAGE_GALLERY_FILTERS.searchQuery,
  category = DEFAULT_IMAGE_GALLERY_FILTERS.category,
} = {}) {
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS)
  const activeFilters = { searchQuery: debouncedSearchQuery, category }

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    // Filters live in the key so any change drops the accumulated pages and
    // restarts from page 1; keepPreviousData keeps the old grid up (dimmed)
    // until the new first page lands.
    queryKey: ['communityGalleryImages', activeFilters],
    queryFn: ({ pageParam = 1 }) => fetchCommunityImages({ page: pageParam, ...activeFilters }),
    getNextPageParam,
    initialPageParam: 1,
    keepPreviousData: true,
  })

  const images = data?.pages?.flatMap(page => page.items) ?? []

  // Returns the fetch so a caller can act on the appended page - the modal steps into the
  // first image of it once it lands. The scroll sentinel ignores the result.
  const handleLoadMore = useCallback(() => fetchNextPage(), [fetchNextPage])

  return {
    images,
    hasNextPage: hasNextPage ?? false,
    handleLoadMore,
    isFetchingNextPage,
    isLoading,
    // Any in-flight request that is neither the first load nor a next page is a
    // filter-driven refetch, which is the only case the grid dims for.
    isRefetching: isFetching && !isFetchingNextPage && !isLoading,
    isError,
    activeFilters,
  }
}

export default useGalleryImages
